import { EntityTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { workspaceTypeFromEntityType } from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsDependencyContext } from '../../shared/types';

/**
 * When the entity type changes: clear represents-clients for Individuals,
 * recompute the proceed gate, and re-derive workspaceType.
 * Mirrors the legacy reset effect at
 * packages/frontend/src/components/WorkspaceDetailsForm.tsx:300-324
 * (reset on dirtyFields.entityType).
 */
export const useEntityTypeDependencies = ({
  form,
}: WorkspaceDetailsDependencyContext) => {
  const { control, formState, getValues, setValue } = form;
  const { isDirty } = formState;
  const entityType = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.entityType,
  });
  const previous = useRef(entityType);

  useEffect(() => {
    if (previous.current === entityType) return;
    previous.current = entityType;

    if (!isDirty) return;

    const country = getValues(
      WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
    );
    const entityName = getValues(WORKSPACE_DETAILS_FORM_FIELDS.entityName);

    setValue(
      WORKSPACE_DETAILS_FORM_FIELDS.canProceed,
      country !== 'AU' || Boolean(entityName),
      { shouldValidate: true },
    );

    if (entityType === EntityTypeEnum.Individual) {
      setValue(WORKSPACE_DETAILS_FORM_FIELDS.representsClients, '', {
        shouldValidate: true,
      });
    }

    if (entityType) {
      const representsClients =
        entityType === EntityTypeEnum.Individual
          ? ''
          : getValues(WORKSPACE_DETAILS_FORM_FIELDS.representsClients);

      setValue(
        WORKSPACE_DETAILS_FORM_FIELDS.workspaceType,
        workspaceTypeFromEntityType(entityType, representsClients),
      );
    }
  }, [entityType, getValues, isDirty, setValue]);

  return entityType;
};
